import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { HandHeart, IndianRupee, Users, ShieldCheck, Mail, Phone, Calendar, Download, Search } from "lucide-react";

export default function NgoDonations() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/partner/ngo/donations`).then(r => setData(r.data)).catch(() => setData("err"));
  }, []);

  if (!user) return null;
  if (data === null) return <div className="text-ink-soft">Loading…</div>;
  if (data === "err") return <div className="text-ink-soft">No NGO profile found. Visit <a href="/app/partner" className="text-brand font-bold">Settings</a> first.</div>;

  const items = data.items || [];
  const filtered = q
    ? items.filter(d => [d.donor_name, d.donor_email, d.donation_id].some(v => (v || "").toLowerCase().includes(q.toLowerCase())))
    : items;

  const exportCsv = () => {
    const rows = [["donation_id", "date", "donor_name", "donor_email", "donor_phone", "amount_inr", "80g_eligible", "note"]];
    items.forEach(d => rows.push([d.donation_id, d.created_at, d.donor_name, d.donor_email || "", d.donor_phone || "", d.amount_inr, d.is_80g_eligible ? "Y" : "N", (d.note || "").replace(/[\r\n,]/g, " ")]));
    const csv = rows.map(r => r.map(c => `"${(c == null ? "" : String(c)).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `donations-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const top = [...items].sort((a, b) => (b.amount_inr || 0) - (a.amount_inr || 0)).slice(0, 3);

  return (
    <div className="space-y-6" data-testid="ngo-donations-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold inline-flex items-center gap-1"><HandHeart className="w-4 h-4"/>NGO · donations received</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Donor wall</h1>
          <p className="text-ink-soft text-sm">Every donation, with 80G eligibility, donor info, and one-click CSV export for your books.</p>
        </div>
        <button onClick={exportCsv} disabled={items.length === 0} data-testid="export-csv" className="bg-ink text-white rounded-full px-4 py-2.5 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-40 hover:bg-brand">
          <Download className="w-4 h-4"/> Export CSV
        </button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Tile icon={IndianRupee} label="Total raised" value={`₹${(data.total_inr || 0).toLocaleString("en-IN")}`} color="bg-brand/15 text-brand"/>
        <Tile icon={Users}       label="Donors"        value={data.count || 0}                                color="bg-leaf/40 text-ink"/>
        <Tile icon={ShieldCheck} label="80G eligible"  value={items.filter(d => d.is_80g_eligible).length}    color="bg-sky/15 text-sky"/>
      </div>

      {top.length > 0 && (
        <section className="bg-gradient-to-br from-sun/50 to-sun/20 border border-sun rounded-3xl p-5">
          <h2 className="font-heading text-lg font-bold mb-3">Top donors this month</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {top.map(d => (
              <div key={d.donation_id} className="bg-surface rounded-2xl p-3 border border-line">
                <div className="font-heading font-extrabold text-lg">₹{(d.amount_inr || 0).toLocaleString("en-IN")}</div>
                <div className="text-xs font-bold mt-0.5">{d.donor_name}</div>
                {d.donor_email && <div className="text-[10px] text-ink-soft truncate">{d.donor_email}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="font-heading text-xl font-bold flex-1">All donations</h2>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search donor..." data-testid="donor-search" className="pl-8 pr-3 py-1.5 rounded-full bg-surface-muted/50 border border-line text-xs font-bold w-44 focus:border-brand outline-none"/>
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="text-ink-soft text-sm">No donations to show. Share your NGO link with supporters to start receiving.</p>
        ) : (
          <div className="divide-y divide-line">
            {filtered.map(d => (
              <div key={d.donation_id} data-testid={`donation-${d.donation_id}`} className="py-3 flex items-center justify-between gap-3 flex-wrap text-sm">
                <div className="flex-1 min-w-[180px]">
                  <div className="font-heading font-extrabold inline-flex items-center gap-1.5">{d.anonymous ? "Anonymous" : d.donor_name}
                    {d.is_80g_eligible && <span className="text-[9px] font-bold uppercase tracking-wider bg-brand-green text-white rounded-full px-2 py-0.5">80G</span>}
                  </div>
                  <div className="text-xs text-ink-soft inline-flex items-center gap-2 mt-0.5">
                    <Calendar className="w-3 h-3"/>{new Date(d.created_at).toLocaleDateString()}
                    {d.donor_email && <><Mail className="w-3 h-3 ml-1"/>{d.donor_email}</>}
                    {d.donor_phone && <><Phone className="w-3 h-3 ml-1"/>{d.donor_phone}</>}
                  </div>
                  {d.note && <div className="text-xs italic text-ink-soft mt-1">"{d.note}"</div>}
                </div>
                <span className="font-heading text-xl font-extrabold inline-flex items-center"><IndianRupee className="w-4 h-4"/>{(d.amount_inr || 0).toLocaleString("en-IN")}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Tile({ icon: I, label, value, color }) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-4 shadow-soft">
      <div className={`w-9 h-9 rounded-xl grid place-items-center ${color}`}><I className="w-4 h-4"/></div>
      <div className="text-[11px] uppercase tracking-wider font-bold text-ink-soft mt-2">{label}</div>
      <div className="font-heading text-2xl font-extrabold mt-0.5">{value}</div>
    </div>
  );
}
